import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { RevealImage, AssetPlaceholder } from "@/components/RevealImage";
import JsonLd from "@/components/JsonLd";
import { TeacherSection, NextSocial, FaqSection } from "@/components/HomeSections";
import { organizationSchema, personSchema, faqSchema } from "@/seo/schema";

const Home = () => {
  return (
    <>
      <SEO path="/" />
      <JsonLd data={organizationSchema()} />
      <JsonLd data={personSchema()} />
      <JsonLd data={faqSchema()} />

      {/* 1. HERO — Ivory, centred */}
      <section className="bg-ivory" data-testid="home-hero">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16 md:pb-24 text-center">
          <p className="eyebrow reveal reveal-1" data-testid="hero-eyebrow">
            Ancient knowledge · Present practice
          </p>
          <h1
            className="reveal reveal-2 mt-5 font-serif font-light text-temple text-[2.5rem] sm:text-[3.5rem] md:text-[5rem] lg:text-[5.75rem] leading-[0.92] max-w-5xl mx-auto"
            data-testid="hero-headline"
          >
            <span className="sr-only">Shiva Murti — a living Balinese healing lineage.</span>
            <span aria-hidden="true">A living lineage.</span>
          </h1>
          <p
            className="reveal reveal-3 mt-7 font-sans font-light text-temple text-base md:text-lg leading-[1.8] max-w-prose mx-auto"
            data-testid="hero-subline"
          >
            Rooted in Balinese ceremony — healing to receive, a path to enter.
          </p>
          <div className="reveal reveal-4 mt-10 flex justify-center">
            <hr className="hairline w-24" />
          </div>
        </div>
      </section>

      {/* 2. THE TWO DOORS — editorial split per panel: image above,
          Temple-Black band below (text NOT overlaid on artwork).
          1px Sacred Gold seam between panels (Poleng — structural only). */}
      <section
        aria-label="The two doors"
        className="bg-ivory"
        data-testid="home-two-doors"
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1px_1fr] gap-0">
          {/* LEFT — RECEIVE */}
          <article className="flex flex-col" data-testid="door-receive">
            <div className="relative overflow-hidden aspect-[4/3] md:aspect-[5/4] grain">
              <RevealImage
                src="/assets/melukat-blessing.png"
                alt="Melukat water-blessing scene at Ratu Nabe's private temple"
                className="absolute inset-0"
                imgClassName="w-full h-full object-cover"
                loading="eager"
                fetchpriority="high"
              />
            </div>
            <div className="bg-temple text-ivory px-8 md:px-16 lg:px-24 py-14 md:py-20 flex-1 flex flex-col items-center text-center">
              <p className="eyebrow">The first door</p>
              <h2 className="mt-4 font-serif font-light text-ivory text-5xl md:text-7xl leading-[0.92]">
                Receive
              </h2>
              <p className="mt-6 font-sans font-light text-ivory/85 text-base md:text-lg leading-[1.7] max-w-prose mx-auto">
                Healing and ceremony — come to be cleansed, blessed, restored.
              </p>
              <div className="mt-auto pt-10">
                <Link
                  to="/receive"
                  className="inline-flex items-center gap-3 px-7 py-4 bg-ivory text-temple border border-gold font-sans text-[0.72rem] tracking-[0.28em] uppercase hover:bg-temple hover:text-ivory transition-colors duration-500"
                  data-testid="door-receive-cta"
                >
                  Enter
                  <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </article>

          {/* Sacred-Gold Poleng seam */}
          <div
            aria-hidden
            className="hidden md:block w-px bg-gold"
            data-testid="poleng-seam"
          />
          <div aria-hidden className="md:hidden h-px w-full bg-gold" />

          {/* RIGHT — BECOME (polar opposite: Ivory band + Temple-Black text) */}
          <article className="flex flex-col" data-testid="door-become">
            <div className="relative overflow-hidden aspect-[4/3] md:aspect-[5/4] grain">
              <RevealImage
                src="/assets/garuda-wisnu.png"
                alt="Garuda Wisnu Kencana — Vishnu's mount and the Balinese path of devotion"
                className="absolute inset-0"
                imgClassName="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div className="bg-ivory text-temple px-8 md:px-16 lg:px-24 py-14 md:py-20 flex-1 flex flex-col items-center text-center">
              <p className="eyebrow">The second door</p>
              <h2 className="mt-4 font-serif font-light text-temple text-5xl md:text-7xl leading-[0.92]">
                Become
              </h2>
              <p className="mt-6 font-sans font-light text-temple/80 text-base md:text-lg leading-[1.7] max-w-prose mx-auto">
                The path of Shiva Murti — be initiated into the teaching.
              </p>
              <div className="mt-auto pt-10">
                <Link
                  to="/become"
                  className="inline-flex items-center gap-3 px-7 py-4 bg-temple text-ivory border border-gold font-sans text-[0.72rem] tracking-[0.28em] uppercase hover:bg-ivory hover:text-temple transition-colors duration-500"
                  data-testid="door-become-cta"
                >
                  Enter
                  <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Section separator — 1px Sacred Gold hairline between the two doors and the lineage */}
      <hr className="hairline" data-testid="home-section-divider" />

      {/* 3. A BREATH OF THE LINEAGE — narrow, centred */}
      <section className="bg-ivory" data-testid="home-lineage-breath">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <p className="eyebrow">The lineage</p>
          <p className="mt-7 font-serif font-light text-temple text-2xl md:text-3xl leading-[1.4]">
            Shiva Murti descends from <span className="text-gold">Ratu Nabe</span> —
            high priest, and the creator of this lineage. What he carries is not taught
            from books but transmitted through ceremony, sacred water, and initiation.
          </p>
          <div className="mt-10">
            <Link to="/lineage" className="btn-ghost" data-testid="lineage-cta">
              Enter the lineage
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>

      <TeacherSection />
      <NextSocial />
      <FaqSection />
    </>
  );
};

export default Home;
